import { Tooltip } from "@material-ui/core";
import { darken, lighten } from "@material-ui/core/styles/colorManipulator";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { Assistant } from "@material-ui/icons";
import * as React from "react";

// util
import { ResponseStatus } from "@clockwise/client-commons/src/util/attendees";
import { maybeShowMinutes, prettyTimeRange } from "@clockwise/client-commons/src/util/date";
import {
  AnnotatedEvent,
  isClockwiseUnsyncedEvent,
  isFocusTimeSmartHold,
  isLunchSmartHold,
  isTravelTimeSmartHold,
} from "@clockwise/client-commons/src/util/event";
import {
  getEventColor,
  IEventCategoryColoring,
  IEventColoringSettings,
} from "@clockwise/client-commons/src/util/event-category-coloring";
import { EventPositioner, IEvent } from "@clockwise/client-commons/src/util/event-position";
import { isAutopilotExplicitlyEnabled } from "@clockwise/client-commons/src/util/event-tag";
import { ZonedMoment } from "@clockwise/client-commons/src/util/time-zone";

// internal
import { styles } from "./CalendarDayEvent.styles";

// components
import { DataMapper } from "@components/relative-view";
import { TutorialTip } from "@components/tutorial-tip";
import { TipPosition } from "@components/tutorial-tip/TutorialTip";

// styles
import { colors, greys } from "@styles/color.styles";
import { fontFamily } from "@styles/type.styles";

export interface CalendarDayEventTooltipOptions {
  header: string;
  body?: string;
  dismissText?: string;
  moreText?: string;
  onDismiss?: () => void;
  onMore?: () => void;
  tipPosition?: TipPosition;
  tipColor?: string;
}

export interface CalendarDayIAttendee {
  responseStatus: ResponseStatus;
  urnValue: string;
}

export interface CalendarDayIEvent extends IEvent {
  calendarIds: string[];
  location?: string;
  title: string | null;
  organizer: CalendarDayIAttendee;
  attendees: CalendarDayIAttendee[];
  annotatedEvent: AnnotatedEvent | null;
  annotatedRecurringEvent: AnnotatedEvent | null;
  isClockwiseEvent: boolean;
}

export type CalendarDayEventMode = "standard" | "simple" | "simple-bold";

interface CalendarDayEventContainer {
  onClick?: (calendarId: string, externalEventId: string) => void;
  eventPositioner: EventPositioner<CalendarDayIEvent>;
  userId: string;
  calendarIds: string[];
  defaultColor?: string;
  colorSettings?: IEventColoringSettings<IEventCategoryColoring>;
  containerClassName?: string;
  zIndexOverride?: number;
  mode: CalendarDayEventMode;
  tooltipOptions?: CalendarDayEventTooltipOptions;
}

export interface CalendarDayEventProps
  extends WithStyles<typeof styles>,
    CalendarDayEventContainer {
  showFocusTimeHolds: boolean;
  showLunchHolds: boolean;
  showNiceSmartHolds: boolean;
}

export interface CalendarDayEventState {
  isHovered: boolean;
}

export interface CalendarDayDataMapperGeneratorOptions {
  onClick?: (calendarId: string, externalEventId: string) => void;
  userId: string;
  calendarIds: string[];
  showFocusTimeHolds?: boolean;
  showLunchHolds?: boolean;
  showNiceSmartHolds?: boolean;
  defaultColor?: string;
  colorSettings?: IEventColoringSettings<IEventCategoryColoring>;
  mode?: CalendarDayEventMode;
  containerClassName?: string;
  zIndexOverride?: number;
  tooltipOptions?: CalendarDayEventTooltipOptions;
}

export interface CalendarDayEventDisplayData {
  eventPositioner: EventPositioner<CalendarDayIEvent>;
}

export const BASE_HEIGHT = 60; // used for computing the height of events, hour distance, etc

class CalendarDayEventComponent extends React.Component<
  CalendarDayEventProps,
  CalendarDayEventState
> {
  static defaultProps = {
    mode: "simple-bold" as CalendarDayEventMode,
    showFocusTimeHolds: true,
    showLunchHolds: true,
    showNiceSmartHolds: false,
  };

  constructor(props: CalendarDayEventProps) {
    super(props);

    this.state = {
      isHovered: false,
    };
  }

  private onClick = () => {
    const { event } = this.props.eventPositioner;
    const matchedCalendarId = this.props.calendarIds.find(
      (calId) => event.calendarIds.indexOf(calId) !== -1,
    );
    const calendarId = matchedCalendarId ? matchedCalendarId : null;

    if (calendarId && this.props.onClick) {
      const externalEventId = event.eventKey.externalEventId;

      this.props.onClick(calendarId, externalEventId);
    }
  };

  private onHover = (isHovered: boolean) => {
    this.setState({ isHovered });
  };

  public renderStandard() {
    const { classes } = this.props;
    const { calendarIds, userId, colorSettings } = this.props;
    const { event, start, end, top, left, height, width, floatOffset } = this.props.eventPositioner;

    const timeRangeAndMaybeLocation = `${prettyTimeRange(start, end)}${
      event.location ? `, ${event.location}` : ""
    }`;

    const calcLeft = this.state.isHovered ? 0 : floatOffset ? left + floatOffset : left;

    const calcWidth = this.state.isHovered ? 100 : floatOffset ? width - floatOffset : width;

    const eventContainerStyle = {
      top: `${top}%`,
      height: `${height}%`,
      left: `${calcLeft}%`,
      width: `${calcWidth}%`,
    };

    let youAttendee: CalendarDayIAttendee | undefined;
    if (calendarIds.indexOf(event.organizer?.urnValue) !== -1) {
      youAttendee = event.organizer;
    } else if (event.attendees) {
      youAttendee = event.attendees.find(
        (attendee) => calendarIds.indexOf(attendee.urnValue) !== -1,
      );
    }

    const needsAction = youAttendee && youAttendee.responseStatus === "NeedsAction";

    const hasOccurred = new ZonedMoment().isSameOrAfter(
      new ZonedMoment(event.endTime.dateOrDateTime),
    );

    if (isClockwiseUnsyncedEvent(event)) {
      // No longer rendering unsynced events.
      return null;
    }

    const isLunch = isLunchSmartHold(event);
    const isFocusTime = isFocusTimeSmartHold(event);
    const isTravelTime = isTravelTimeSmartHold(event);

    const color = getEventColor(event, colorSettings, userId, isFocusTime);

    const apColor = needsAction ? color : greys.white;

    let eventStyle = {
      ...styles.event,
      opacity: hasOccurred ? 0.5 : 1,
      backgroundColor: this.state.isHovered ? lighten(color, 0.25) : color,
    };

    if (needsAction) {
      eventStyle = {
        ...styles.event,
        color,
        opacity: hasOccurred ? 0.5 : 1,
        border: `1px solid ${color}`,
        backgroundColor: !this.state.isHovered ? greys.white : lighten(color, 0.9),
      };
    }

    const autopilotEnabled =
      isAutopilotExplicitlyEnabled(event) || isFocusTime || isLunch || isTravelTime;

    // calculate the height of the actual area the text can live
    const mHeight = (height / 100) * (24 * BASE_HEIGHT);

    // calculate the max height of the title text
    // height of actual area - 8 top padding - 8 bottom padding - 4 padding bottom internal - 2 border width
    //   - size of one line of text - any extra space outside of line height modulo
    const maxHeight = Math.max(
      mHeight - 16 - 4 - 4 - 2 - 12 * 1.1 - (mHeight % (12 * 1.1)),
      12 * 1.1,
    );

    // only show locationOrEndTime if there is enough space for it
    const displayLocationOrEndTime = mHeight <= 48 ? "none" : "block";

    return (
      <div className={classes.container} style={eventContainerStyle}>
        <div style={styles.eventMask}>
          <div
            onClick={this.onClick}
            onMouseOver={() => this.onHover(true)}
            onMouseOut={() => this.onHover(false)}
            style={eventStyle}
          >
            <div style={{ display: "flex", flexDirection: "row" }}>
              <div style={{ flex: 1, overflow: "hidden" }}>
                <div
                  style={{
                    ...styles.eventTitle,
                    width: autopilotEnabled ? "calc(100% - 20px)" : "100%",
                    maxHeight: `${maxHeight}px`,
                  }}
                >
                  <span
                    style={{
                      fontFamily,
                      fontWeight: "bold",
                    }}
                  >
                    {event.title || "(No title)"}
                  </span>
                  {mHeight <= 48 && !event.isAllDay ? `, ${maybeShowMinutes(start, true)}` : ""}
                </div>
              </div>
              {autopilotEnabled ? (
                <Tooltip title="Autopilot enabled">
                  <Assistant htmlColor={apColor} style={styles.eventAPIcon} />
                </Tooltip>
              ) : null}
            </div>
            <div style={{ ...styles.eventLocationTime, display: displayLocationOrEndTime }}>
              {timeRangeAndMaybeLocation}
            </div>
          </div>
        </div>
      </div>
    );
  }

  public renderSimple() {
    const { isHovered } = this.state;
    const {
      userId,
      colorSettings,
      zIndexOverride,
      classes,
      tooltipOptions,
      showNiceSmartHolds,
    } = this.props;
    const { event, top, left, height, width, floatOffset } = this.props.eventPositioner;

    const calcLeft =
      this.state.isHovered && this.props.onClick ? 0 : floatOffset ? left + floatOffset : left;

    const calcWidth =
      this.state.isHovered && this.props.onClick ? 100 : floatOffset ? width - floatOffset : width;

    const eventContainerStyle = {
      top: `${top}%`,
      height: `${height}%`,
      left: `${calcLeft}%`,
      width: `${calcWidth}%`,
    };

    const isLunch = isLunchSmartHold(event);
    const isFocusTime =
      isFocusTimeSmartHold(event) || (event.title || "").indexOf("FocusTime") !== -1;
    const autopilotEnabled = isAutopilotExplicitlyEnabled(event);
    const clockwiseUnsyncedEvent = isClockwiseUnsyncedEvent(event);

    let color = getEventColor(event, colorSettings, userId, false, this.props.defaultColor);
    let backgroundColor = color;
    let borderColor = color;
    let title = event.title || "(No title)";
    let borderStyle = "solid";
    let shouldShow = true;

    if (clockwiseUnsyncedEvent && !showNiceSmartHolds) {
      // No longer displaying unsynced events other than when
      // showNiceSmartHolds is true during onboarding.
      return null;
    } else if (isFocusTime) {
      if (showNiceSmartHolds) {
        color = colorSettings ? greys.white : colors.blueSky.darker;
        backgroundColor = colorSettings
          ? getEventColor(event, colorSettings, userId, isFocusTime)
          : colors.blueSky.darker;
        borderColor = backgroundColor;
      }
      title = "❇️ Focus Time";
      borderStyle = "dashed";
      shouldShow = this.props.showFocusTimeHolds;
    } else if (isLunch) {
      if (showNiceSmartHolds) {
        color = colorSettings ? greys.white : colors.birch.standard;
        backgroundColor = colorSettings ? colors.googleLavendar : colors.birch.standard;
        borderColor = color;
      }
      title = "❇️ Lunch Hold";
      borderStyle = "dashed";
      shouldShow = this.props.showLunchHolds;
    }

    const opacity = shouldShow ? 1 : 0;
    const transform = shouldShow ? "scale(1, 1)" : "scale(1, 0)";

    let colorStyle;
    if (this.props.mode === "simple") {
      colorStyle = {
        background: lighten(backgroundColor, 0.7),
        border: `1px ${borderStyle} ${borderColor}`,
        color: darken(color, 0.1),
      };
    } else {
      colorStyle = clockwiseUnsyncedEvent
        ? {
            background:
              !isHovered || !this.props.onClick ? backgroundColor : lighten(backgroundColor, 0.3),
            border: `1px ${borderStyle} ${borderColor}`,
            color: darken(color, 0.1),
          }
        : {
            background:
              !isHovered || !this.props.onClick ? backgroundColor : lighten(backgroundColor, 0.3),
            border: left + floatOffset !== 0 ? `1px solid ${greys.white}` : undefined,
          };
    }

    return (
      <React.Fragment>
        <div
          style={{
            ...eventContainerStyle,
            position: "absolute",
            zIndex: (zIndexOverride ?? 3) + (isHovered && this.props.onClick ? 1 : 0),
            paddingRight: 5,
            paddingBottom: 2,
          }}
        >
          <div
            className={`${classes.simpleEvent} ${this.props.containerClassName ?? ""}`}
            style={{
              opacity,
              transform,
              cursor: this.props.onClick ? "pointer" : undefined,
              position: "relative",
              height: "100%",
              transition: this.props.onClick ? "unset" : undefined,
              ...colorStyle,
            }}
            onMouseOver={() => this.onHover(true)}
            onMouseOut={() => this.onHover(false)}
            onClick={this.onClick}
          >
            {autopilotEnabled ? `❇️ ` : null}
            {title.replace(" (via Clockwise)", "")}
          </div>
        </div>
        {tooltipOptions
          ? (() => {
              let tipTransform: string = "";
              let tipTop: string = "";
              let tipLeft: string = "";
              let tipRight: string = "";

              if (tooltipOptions.tipPosition === "left") {
                tipTransform = "translate(calc(100% + 5px), -40px)";
                tipRight = `${left}%`;
                tipTop = `${top + height / 2}%`;
              } else {
                // assume right, no other positions are supported
                tipTransform = "translate(calc(-100% - 10px), -40px)";
                tipLeft = `${left}%`;
                tipTop = `${top + height / 2}%`;
              }

              return (
                <TutorialTip
                  containerStyle={{
                    top: tipTop,
                    left: tipLeft,
                    right: tipRight,
                    transform: tipTransform,
                    zIndex: 4,
                  }}
                  hide={false}
                  onDismiss={tooltipOptions.onDismiss}
                  onMore={tooltipOptions.onMore}
                  moreText={tooltipOptions.moreText}
                  dismissText={tooltipOptions.dismissText}
                  tipPosition={tooltipOptions.tipPosition}
                  tipColor={tooltipOptions.tipColor}
                  tooltipContents={
                    <React.Fragment>
                      <div className={classes.tipHeader}>{tooltipOptions.header}</div>
                      {tooltipOptions.body ? (
                        <div className={classes.tipBody}>{tooltipOptions.body}</div>
                      ) : null}
                    </React.Fragment>
                  }
                />
              );
            })()
          : null}
      </React.Fragment>
    );
  }

  public render() {
    switch (this.props.mode) {
      case "simple":
      case "simple-bold":
        return this.renderSimple();
      case "standard":
      default:
        return this.renderStandard();
    }
  }
}

export const CalendarDayEvent = withStyles(styles)(CalendarDayEventComponent);

export const calendarDayEventDataMapperFactory = (options: CalendarDayDataMapperGeneratorOptions) =>
  ((displayData: CalendarDayEventDisplayData) => (
    <CalendarDayEvent {...options} {...displayData} />
  )) as DataMapper<CalendarDayEventDisplayData>;
